import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";

const RADIUS = 22;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const splitSentences = (text) => {
  if (!text) return [];
  const result = [];
  for (const line of text.split(/\n/)) {
    // Within a line, split only when .!? is followed by a space or tab —
    // so "domain.de" stays intact but "Hello. World." becomes two slides.
    for (const part of line.split(/(?<=[.!?])(?=[ \t])/)) {
      const trimmed = part.trim();
      if (trimmed) result.push(trimmed);
    }
  }
  return result;
};

const Presentation = forwardRef(({ text, fontSize = 48, sentenceDuration = 5, mirrorHorizontal = false, countdownInit = 5, startMode = "countdown", showNextSentence = false }, ref) => {
  const sentences = useMemo(() => splitSentences(text), [text]);

  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [timer, setTimer] = useState(sentenceDuration);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [awaitingStart, setAwaitingStart] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const exitFullscreen = () => {
    const fn =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;
    if (fn) fn.call(document);
  };

  const containerRef = useRef(null);
  const indexRef = useRef(0);
  const timerRef = useRef(sentenceDuration);
  const durationRef = useRef(sentenceDuration);
  const countdownRef = useRef(null);

  // Keep durationRef in sync so the interval always uses the latest value
  durationRef.current = sentenceDuration;

  const goFullscreen = async () => {
    const el = containerRef.current;
    if (!el || document.fullscreenElement) return;
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen;
    try {
      if (req) await req.call(el);
    } catch (_) {}
  };

  const resetSlides = () => {
    indexRef.current = 0;
    timerRef.current = durationRef.current;
    setIndex(0);
    setTimer(durationRef.current);
  };

  const beginCountdown = () => {
    countdownRef.current = countdownInit;
    setCountdown(countdownInit);
    setIsCountingDown(true);
  };

  const start = async () => {
    await goFullscreen();
    setActive(false);
    setIsCountingDown(false);
    setCountdown(null);
    resetSlides();

    if (startMode === "countdown") {
      beginCountdown();
      setAwaitingStart(false);
    } else {
      setAwaitingStart(true);
    }
  };

  const handleInDisplayStart = () => {
    setAwaitingStart(false);
    if (startMode === "countdownAndStartbutton") {
      beginCountdown();
    } else {
      setActive(true);
    }
  };

  const pause = () => setActive(false);
  const play = () => setActive(true);
  const toggle = () => setActive((a) => !a);

  const restart = () => {
    setActive(false);
    setIsCountingDown(false);
    setCountdown(null);
    setAwaitingStart(false);
    resetSlides();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => ({ start, pause, play, toggle, restart }), [startMode, countdownInit]);

  // Countdown -> activate slideshow
  useEffect(() => {
    if (!isCountingDown) return;
    const id = setInterval(() => {
      countdownRef.current -= 1;
      if (countdownRef.current <= 0) {
        clearInterval(id);
        setIsCountingDown(false);
        setCountdown(null);
        setActive(true);
      } else {
        setCountdown(countdownRef.current);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [isCountingDown]);

  // Tick down the timer
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      timerRef.current -= 1;
      if (timerRef.current <= 0) {
        const next = indexRef.current + 1;
        if (next >= sentences.length) {
          setActive(false);
          return;
        }
        indexRef.current = next;
        timerRef.current = durationRef.current;
        setIndex(next);
        setTimer(durationRef.current);
      } else {
        setTimer(timerRef.current);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [active, sentences.length]);

  // SVG ring progress: full at sentenceDuration, empty at 0s
  const progress = timer / sentenceDuration;
  const dashOffset = CIRCUMFERENCE * (1 - progress);

  const sentence = sentences[index] ?? "";
  const nextSentence = sentences[index + 1] ?? "";

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0d0d0d",
        borderRadius: "8px",
        padding: "40px",
      }}
    >
      {/* Exit fullscreen button */}
      {isFullscreen && (
        <button
          onClick={exitFullscreen}
          title="Exit fullscreen"
          style={{
            position: "absolute",
            top: 26,
            right: 90,
            zIndex: 20,
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: "none",
            cursor: "pointer",
            background: "rgba(0,0,0,0.5)",
            color: "#fff",
            fontSize: 22,
            fontWeight: 700,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ×
        </button>
      )}

      {/* Timer — upper right */}
      <div
        style={{
          position: "absolute",
          top: "16px",
          right: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="60" height="60">
          {/* Track */}
          <circle
            cx="30"
            cy="30"
            r={RADIUS}
            fill="none"
            stroke="#333"
            strokeWidth="4"
          />
          {/* Progress ring */}
          <circle
            cx="30"
            cy="30"
            r={RADIUS}
            fill="none"
            stroke="#f5c518"
            strokeWidth="4"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            transform="rotate(-90 30 30)"
            style={{ transition: "stroke-dashoffset 0.9s linear" }}
          />
          {/* Number */}
          <text
            x="30"
            y="35"
            textAnchor="middle"
            fill="#f5c518"
            fontSize="16"
            fontWeight="700"
          >
            {timer}
          </text>
        </svg>
      </div>

      {/* Sentence counter — upper left */}
      {sentences.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            color: "#555",
            fontSize: "14px",
          }}
        >
          {index + 1} / {sentences.length}
        </div>
      )}

      {/* Current sentence, with an optional preview of what comes next so the
          speaker can see where the sentence is heading instead of being left
          hanging at the end of it. */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: `${Math.round(fontSize * 0.5)}px`,
          maxWidth: "80%",
          transform: mirrorHorizontal ? "scaleX(-1)" : undefined,
        }}
      >
        <p
          style={{
            fontSize: `${fontSize}px`,
            color: "#fff",
            textAlign: "center",
            lineHeight: 1.4,
            margin: 0,
          }}
        >
          {sentence}
        </p>
        {showNextSentence && nextSentence && (
          <p
            style={{
              fontSize: `${Math.round(fontSize * 0.55)}px`,
              color: "rgba(255,255,255,0.38)",
              textAlign: "center",
              lineHeight: 1.4,
              margin: 0,
            }}
          >
            {nextSentence}
          </p>
        )}
      </div>

      {/* Countdown overlay */}
      {isCountingDown && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.6)",
            zIndex: 10,
          }}
        >
          <div style={{ fontSize: "6rem", color: "#fff", fontWeight: 700, lineHeight: 1 }}>
            {countdown}
          </div>
        </div>
      )}

      {/* In-display start button overlay */}
      {awaitingStart && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.6)",
            zIndex: 11,
          }}
        >
          <button
            onClick={handleInDisplayStart}
            style={{
              padding: "18px 48px",
              borderRadius: 12,
              border: "none",
              cursor: "pointer",
              background: "#038edc",
              color: "#fff",
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 700,
              fontSize: 22,
              boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
            }}
          >
            Start
          </button>
        </div>
      )}
    </div>
  );
});

Presentation.displayName = "Presentation";
export default Presentation;
